class CartDrawerExtraServices extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hidden: 'hidden',
      active: 'active',
      open: 'open',
      toggleButton: '.extra-services__button',
      descriptionToggleButtons: '.extra-service__description-btn',
      contentContainer: '.extra-services__content',
      serviceHeader: '.extra-service__header',
      serviceCheckboxes: '.extra-service__input',
      serviceContentContainer: '.extra-service__content',
      serviceDescription: '.extra-service__description',
      spinner: '.loading-overlay__spinner',
    };

    this.toggleButton = this.querySelector(this.classes.toggleButton);
    this.descriptionToggleButtons = this.querySelectorAll(this.classes.descriptionToggleButtons);
    this.contentContainer = this.querySelector(this.classes.contentContainer);
    this.notificationContainer = document.querySelector('.extra-services__notification');
    this.serviceCheckboxes = Array.from(this.querySelectorAll(this.classes.serviceCheckboxes));
    this.handlingFeeVariant =
      this.querySelector(`#cart-drawer-extra-service-${window.extraServices.low_order_value_service_fee_product}`)
        ?.dataset?.variantId || null;
    this.handlingFeeMinimum = window.extraServices.low_order_value * 100 || 1500;
    this.isSyncInProgress = false;

    this.serviceCheckboxes.forEach(
      (checkbox) =>
        checkbox.dataset.serviceType !== 'mandatory' &&
        checkbox.addEventListener('change', this.onServiceCheckboxToggle.bind(this))
    );

    this.init();
  }

  init() {
    if (this.dataset.cartItemsCount == 1) {
      // only sync upon new cart load
      this.synchronizeWithCart();
    }

    if (this.toggleButton && this.contentContainer) {
      this.toggleButton.addEventListener('click', this.contentToggleEventHandler.bind(this), 1000);
    }

    if (this.descriptionToggleButtons?.length) {
      this.descriptionToggleButtons.forEach((button) =>
        button.addEventListener('click', (event) => debounce(this.descriptionToggleEventHandler(event), 1000))
      );
    }
  }

  contentToggleEventHandler() {
    if (!this.contentContainer || !this.toggleButton) return;

    this.contentContainer.classList.toggle(this.classes.active);
    this.toggleButton.classList.toggle(this.classes.open);
  }

  descriptionToggleEventHandler(event) {
    const clicked = event.target;
    const parent = clicked.closest(this.classes.serviceContentContainer);
    if (!parent) return;

    const text = parent.querySelector(this.classes.serviceDescription);
    if (!text) return;

    if (parent.classList.contains(this.classes.active)) {
      parent.classList.remove(this.classes.active);
      text.setAttribute('aria-expanded', 'false');
    } else {
      parent.classList.add(this.classes.active);
      text.setAttribute('aria-expanded', 'true');
    }
  }

  synchronizeWithCart() {
    this.fetchCart()
      .then((cart) => {
        this.processCartContents(cart);
      })
      .catch((error) => {
        console.error('Initial cart sync failed:', error);
      })
      .finally(() => (this.isSyncInProgress = false));
  }

  async fetchCart() {
    return fetch(`${window.Shopify.routes.root}cart.js`).then(async (response) => {
      if (!response.ok) throw new Error(response.statusText);
      return response.json();
    });
  }

  async updateCartItem(variantId, quantity) {
    // Prevent extra services sync during variant replacement
    if (this.isVariantReplacementInProgress) {
      return;
    }

    const section = this.getSectionsToRender().map((section) => section.section);
    const data = { sections: section };

    const isAdd = quantity > 0;
    const endpoint = isAdd ? 'cart/add.js' : 'cart/change.js';
    const payload = isAdd
      ? { id: variantId, quantity, sections: data.sections }
      : { id: variantId, quantity: 0, sections: data.sections };

    const checkbox = document.querySelector(`input[data-variant-id="${variantId}"]`);
    const spinner = checkbox?.closest(this.classes.serviceHeader)?.querySelector(this.classes.spinner);
    spinner?.classList.remove(this.classes.hidden);

    return fetch(`${window.Shopify.routes.root}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Cart API error (${response.status}):`, errorText);
          throw new Error(`Cart API failed: ${response.status} ${response.statusText} - ${errorText}`);
        }
        const responseJson = JSON.parse(await response.text());
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: isAdd ? 'cart-items-add' : 'cart-items',
          cartData: responseJson,
        });
        return responseJson;
      })
      .then((parsedResponse) => {
        spinner?.classList.add(this.classes.hidden);
        this.renderContents(parsedResponse);
        return parsedResponse;
      });
  }

  async addItemToCart(variantId) {
    const cartItemsVariantIds = this.dataset.cartItemsVariantIds.split(',');
    if (cartItemsVariantIds.includes(variantId)) {
      return;
    }
    return this.updateCartItem(variantId, 1);
  }

  async removeItemFromCart(variantId) {
    const cartItemsVariantIds = this.dataset.cartItemsVariantIds.split(',');
    if (!cartItemsVariantIds.includes(variantId)) {
      return;
    }
    return this.updateCartItem(variantId, 0);
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      const elementToReplace = sectionElement?.querySelector(section.selector) || sectionElement;
      if (elementToReplace) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }
    });
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: document.getElementById('CartDrawer')?.dataset.id,
        selector: '.cart-drawer__extra-services',
      },
      {
        id: 'CartDrawer',
        section: document.getElementById('CartDrawer')?.dataset.id,
        selector: '.drawer__footer',
      },
      {
        id: 'CartDrawer',
        section: document.getElementById('CartDrawer')?.dataset.id,
        selector: '.drawer__contents.js-contents',
      },
      {
        id: 'main-cart',
        section: document.getElementById('main-cart')?.dataset.id,
        selector: '.main-cart--left',
      },
      {
        id: 'main-cart',
        section: document.getElementById('main-cart')?.dataset.id,
        selector: '.cart__blocks',
      },
      {
        id: 'main-cart',
        section: document.getElementById('main-cart')?.dataset.id,
        selector: '.cart__ctas',
      },
    ];
  }

  onServiceCheckboxToggle(event) {
    const checkbox = event.target;
    const variantId = checkbox.dataset.variantId;
    const operation = checkbox.checked ? this.addItemToCart(variantId) : this.removeItemFromCart(variantId);
    checkbox.disabled = true;
    operation
      .then(() => {
        console.log('Service toggle success.');
      })
      .catch((error) => {
        console.error('Service toggle failed:', error);
        checkbox.checked = !checkbox.checked;
      })
      .finally(() => {
        checkbox.disabled = false;
      });
  }

  updateServices() {
    if (this.isSyncInProgress) return;

    // Prevent extra services sync during variant replacement
    if (this.isVariantReplacementInProgress) {
      return;
    }

    this.isSyncInProgress = true;
    this.fetchCart()
      .then((cart) => {
        return this.processCartContents(cart);
      })
      .catch((error) => {
        console.error('Cart sync error:', error);
      })
      .finally(() => (this.isSyncInProgress = false));
  }

  processCartContents(cart) {
    const serviceIds = this.serviceCheckboxes.map((cb) => cb.dataset.variantId);
    const nonServiceItems = (cart.items || []).filter((item) => !serviceIds.includes(item.variant_id.toString()));
    const cartTotal = nonServiceItems.reduce((sum, item) => sum + (item.final_line_price || item.line_price), 0);

    if (nonServiceItems.length === 0) {
      const removalPromises = this.serviceCheckboxes
        .filter((cb) => cb.checked)
        .map((cb) => {
          cb.checked = false;
          return this.removeItemFromCart(cb.dataset.variantId).then(() => {
            this.showNotification(window.cartActions?.service_removed_html, 'warning', cb.dataset.serviceTitle);
          });
        });
      return Promise.all(removalPromises);
    }

    this.updateHandlingFee(cartTotal, cart.items);
    this.syncServiceCheckboxes(cart.items);
  }

  updateHandlingFee(cartTotal, cartItems) {
    const handlingFeeCheckbox = this.querySelector(`input[data-variant-id="${this.handlingFeeVariant}"]`);
    if (!handlingFeeCheckbox) return;

    const handlingFeePresent = cartItems.some((i) => i.variant_id.toString() === this.handlingFeeVariant);
    const shouldAddHandlingFee = cartTotal < this.handlingFeeMinimum && !handlingFeePresent;
    const shouldRemoveHandlingFee = cartTotal >= this.handlingFeeMinimum && handlingFeePresent;

    if (shouldAddHandlingFee) {
      handlingFeeCheckbox.checked = true;
      this.addItemToCart(this.handlingFeeVariant).then(() =>
        this.showNotification(
          window.cartActions?.service_added_html,
          'warning',
          handlingFeeCheckbox.dataset.serviceTitle
        )
      );
    } else if (shouldRemoveHandlingFee) {
      handlingFeeCheckbox.checked = false;
      this.removeItemFromCart(this.handlingFeeVariant).then(() =>
        this.showNotification(
          window.cartActions?.service_removed_html,
          'success',
          handlingFeeCheckbox.dataset.serviceTitle
        )
      );
    }
  }

  syncServiceCheckboxes(cartItems) {
    const cartVariantIds = cartItems.map((i) => i.variant_id.toString());
    this.serviceCheckboxes.forEach((cb) => {
      cb.checked = cartVariantIds.includes(cb.dataset.variantId);
    });
  }

  showNotification(message, type = 'success', serviceTitle) {
    if (!this.createNotification || !this.createNotificationString) return;
    if (!message || !serviceTitle) return;

    const html = this.createNotificationString(message, serviceTitle, 1);
    this.createNotification(type, html);
  }

  createNotificationString(template, title, quantity) {
    return template.replace('{{ title }}', title).replace('{{ value }}', quantity);
  }

  createNotification(type, messageHtml) {
    if (!this.notificationContainer) return;

    const notificationClass = this.notificationContainer.dataset.notificationClass;
    const notificationSuccessClass = this.notificationContainer.dataset.notificationSuccessClass;
    const notificationWarningClass = this.notificationContainer.dataset.notificationWarningClass;
    const notificationAlertClass = this.notificationContainer.dataset.notificationAlertClass;

    const notification = document.createElement('div');
    notification.classList.add('text-x-small');

    if (type === 'success' && notificationClass && notificationSuccessClass) {
      notification.classList.add(notificationClass, notificationSuccessClass);
    } else if (type === 'warning' && notificationClass && notificationWarningClass) {
      notification.classList.add(notificationClass, notificationWarningClass);
    } else if (type === 'alert' && notificationClass && notificationAlertClass) {
      notification.classList.add(notificationClass, notificationAlertClass);
    }

    notification.innerHTML = messageHtml;
    this.notificationContainer.prepend(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  }
}

customElements.define('cart-drawer-extra-services', CartDrawerExtraServices);

subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
  const cartDrawerExtraServices = document.querySelector('cart-drawer-extra-services');
  if (cartDrawerExtraServices) {
    cartDrawerExtraServices.updateServices();
  }
});
